import type {
  ChartType as CoreChartType,
  SelectableChartType as CoreSelectableChartType,
} from '@revelio/core';

import type { AllSelectionListsWithSkillTaxonomyKey } from '../../../../../shared/selection-lists/types';

export type ChartType = CoreChartType;
export type SelectableChartType = CoreSelectableChartType;

/**
 * `resolveDataType` in `@revelio/core` picks a {@link DataType} (shape). These
 * allowlists still decide which **metric-family** data provider runs: several
 * families produce the same shape (e.g. SINGLE), and some chart types only apply
 * to one family.
 */
function chartTypeGuard<const A extends readonly ChartType[]>(
  allowed: A
): (chartType: ChartType) => chartType is A[number] {
  const set = new Set<string>([...allowed]);
  return (chartType: ChartType): chartType is A[number] => set.has(chartType);
}

const COMPOSITION_CHART_TYPES = [
  'bar',
  'line',
  'stacked-bar',
  'share-of-segment',
  'grouped-bar',
  'diverging-bar',
  'diverging-bar-combo',
  'treemap',
  'table',
  'map',
  'text',
  'gauge',
] as const satisfies readonly ChartType[];

export type CompositionChartType = (typeof COMPOSITION_CHART_TYPES)[number];
export const isCompositionChartType = chartTypeGuard(COMPOSITION_CHART_TYPES);

const TRANSITION_CHART_TYPES = [
  'sankey',
  'chord',
  'grouped-bar',
  'stacked-bar',
  'diverging-bar-combo',
  'table',
  'text',
] as const satisfies readonly ChartType[];

export type TransitionChartType = (typeof TRANSITION_CHART_TYPES)[number];
export const isTransitionChartType = chartTypeGuard(TRANSITION_CHART_TYPES);

const POSTING_CHART_TYPES = [
  'bar',
  'line',
  'grouped-bar',
  'volume',
  'table',
  'text',
  'gauge',
] as const satisfies readonly ChartType[];

export type PostingChartType = (typeof POSTING_CHART_TYPES)[number];
export const isPostingChartType = chartTypeGuard(POSTING_CHART_TYPES);

const SENTIMENT_CHART_TYPES = [
  'bar',
  'line',
  'table',
  'text',
  'diverging-bar',
  'diverging-bar-combo',
  'gauge',
] as const satisfies readonly ChartType[];

export type SentimentChartType = (typeof SENTIMENT_CHART_TYPES)[number];
export const isSentimentChartType = chartTypeGuard(SENTIMENT_CHART_TYPES);

const SCREENER_CHART_TYPES = [
  'table',
  'bar',
  'grouped-bar',
  'line',
  'map',
  'treemap',
  'gauge',
] as const satisfies readonly ChartType[];

export type ScreenerChartType = (typeof SCREENER_CHART_TYPES)[number];
export const isScreenerChartType = chartTypeGuard(SCREENER_CHART_TYPES);

const COMPENSATION_CHART_TYPES = [
  'line',
  'box',
  'distribution',
  'bar',
  'stacked-bar',
  'grouped-bar',
  'table',
  'text',
  'gauge',
] as const satisfies readonly ChartType[];

export type CompensationChartType = (typeof COMPENSATION_CHART_TYPES)[number];
export const isCompensationChartType = chartTypeGuard(COMPENSATION_CHART_TYPES);

export type PrimaryEntity = {
  id: string;
  levelId: AllSelectionListsWithSkillTaxonomyKey;
  name: string;
};

export type PrimaryEntityOption = {
  label: string;
  value: PrimaryEntity;
};
